import type { PincerApp, ViewDiff, ViewNode } from "pincerjs";
import { byId, h } from "./dom";
import { generic, updateControl, describe } from "./generic";
import { DevMode } from "./dev";

/*
The renderer engine: draws the app's tree (pincerjs) into the page, and keeps it up to date one change at a time.
Both web clients are built on it. `access` uses it as it is; `pincerweb` adds designs. See docs/design_notes/clients.md.

Every node gets an element with its compound key as its id, holding its own part (.nodeMain) and its children
(.nodeChildren, id KEY-children). The engine applies each diff from the app: it creates what was added, redraws what
changed, reorders and removes. Controls are updated in place when they're still the same kind of control, so focus,
half-typed text and a screen reader's place survive other people's changes.

How a node is drawn is up to the first design that matches it. A node no design matches is drawn generically, in
place: so anything new in the tree shows up straight away, plain until someone gives it a design. Developer mode
(?dev in the address, or Alt+Shift+D) outlines those nodes and lists them, with the diff log.
*/
export interface Design {
  // Shown to developers, and on the element as data-design
  name: string;
  matches(node: ViewNode, engine: Engine): boolean;
  /*
  Draws the node's own part into `main`, which is empty. Its children are drawn into its children element, each by
  its own design or generically. Use engine.generic(node, main) to start from the plain version.
  */
  draw(node: ViewNode, main: HTMLElement, engine: Engine): void;
  /*
  A whole design draws the node and everything under it, and is redrawn whenever anything under it changes (use
  engine.descendants to read them). Good for a compact card; not for anything holding an input, since redrawing
  would lose what's being typed.
  */
  whole?: boolean;
}

export interface Options {
  designs?:  Design[];
  announce?: HTMLElement;   // a polite live region for other people's changes
}

export class Engine {

  private readonly nodes  = new Map<string, ViewNode>();
  private readonly kids   = new Map<string, string[]>();
  private readonly wholes = new Set<string>();
  private readonly designs: Design[];
  private first = true;
  readonly dev: DevMode;

  constructor(readonly app: PincerApp, private readonly container: HTMLElement, private readonly options: Options = {}) {
    this.designs = options.designs ?? [];
    this.dev = new DevMode();
  }

  start(initialLocation: string) {
    this.app.start(initialLocation);
    this.app.onChange(() => this.update());
    this.update();
  }

  // Everything people do goes through here, to a control on screen
  perform(compoundKey: string, value: string | null) {
    try { this.app.perform(compoundKey, value); }
    catch (problem) { console.log(`Couldn't perform ${compoundKey}: ${problem instanceof Error ? problem.message : problem}`); }
  }

  node(compoundKey: string | null | undefined): ViewNode | undefined {
    return compoundKey == null ? undefined : this.nodes.get(compoundKey);
  }

  // A node's children, in order, as the engine knows them now
  children(node: ViewNode): ViewNode[] {
    return (this.kids.get(node.compoundKey) ?? []).map((key) => this.nodes.get(key)!).filter((child) => child !== undefined);
  }

  // A child by its key (not compound key)
  child(node: ViewNode, key: string): ViewNode | undefined {
    return this.nodes.get(`${node.compoundKey}-${key}`);
  }

  // Everything under a node, depth first
  descendants(node: ViewNode): ViewNode[] {
    const all: ViewNode[] = [];
    const walk = (at: ViewNode) => { for (const child of this.children(at)) { all.push(child); walk(child); } };
    walk(node);
    return all;
  }

  // Draws the plain version of a node into `main`, for designs that only want to add to it
  generic(node: ViewNode, main: HTMLElement) {
    generic(node, main, this);
  }

  /*
  Applies what changed since the last update: first to what the engine knows (so a design can look at a node's
  children, even ones that arrived in the same change), then to the page. Nodes under a whole design aren't drawn
  themselves: their design is redrawn once, at the end.
  */
  update() {
    const diff = this.app.update();
    if (diff.isEmpty()) return;
    const dirty = new Set<string>();
    const describing = new Set<string>();
    const describes = (node: ViewNode) => node.parentKey != null && (node.key === "problem" || node.key === "helpText");

    // What the engine knows
    for (const key of diff.removed) {
      const node = this.nodes.get(key);
      if (node === undefined) continue;
      const whole = this.wholeAbove(node);
      if (whole !== undefined) dirty.add(whole);
      if (describes(node)) describing.add(node.parentKey!);
      const siblings = node.parentKey == null ? undefined : this.kids.get(node.parentKey);
      if (siblings !== undefined) siblings.splice(siblings.indexOf(key), 1);
      this.nodes.delete(key);
      this.kids.delete(key);
      this.wholes.delete(key);
      byId(key)?.remove();
    }
    for (const node of diff.added) {
      this.nodes.set(node.compoundKey, node);
      this.kids.set(node.compoundKey, []);
      if (node.parentKey != null) this.insertKid(node);
      if (describes(node)) describing.add(node.parentKey!);
    }
    for (const node of [...diff.changed, ...diff.reordered]) this.nodes.set(node.compoundKey, node);
    for (const node of diff.reordered) this.kids.set(node.compoundKey, [...node.childKeys]);

    // The page, parents before children
    for (const node of diff.added) {
      const whole = this.wholeAbove(node);
      if (whole !== undefined) { dirty.add(whole); continue; }
      if (node.isOpener) continue;
      this.create(node);
      this.draw(node);
    }
    for (const node of diff.changed) {
      const whole = this.wholeAbove(node);
      if (whole !== undefined) { dirty.add(whole); continue; }
      if (node.isOpener || byId(node.compoundKey) === null) continue;
      this.draw(node);
    }
    for (const node of diff.reordered) {
      const whole = this.wholes.has(node.compoundKey) ? node.compoundKey : this.wholeAbove(node);
      if (whole !== undefined) { dirty.add(whole); continue; }
      const children = byId(`${node.compoundKey}-children`);
      for (const key of node.childKeys) { const element = byId(key); if (element !== null && children !== null) children.append(element); }
    }
    for (const key of dirty) {
      const node = this.nodes.get(key);
      if (node !== undefined) this.draw(node);
    }
    for (const key of describing) this.redescribe(key);

    this.announce(diff);
    this.dev.updated(diff);
    location.hash = this.app.location() ?? "";
  }

  private insertKid(node: ViewNode) {
    const siblings = this.kids.get(node.parentKey!) ?? [];
    this.kids.set(node.parentKey!, siblings);
    const after = node.previousKey == null ? -1 : siblings.indexOf(node.previousKey);
    siblings.splice(after + 1, 0, node.compoundKey);
  }

  // The nearest whole design above a node, if any
  private wholeAbove(node: ViewNode): string | undefined {
    let key = node.parentKey;
    while (key != null) {
      if (this.wholes.has(key)) return key;
      key = this.nodes.get(key)?.parentKey ?? null;
    }
    return undefined;
  }

  private create(node: ViewNode) {
    const element = h("div", { class: "node", id: node.compoundKey },
      h("div", { class: "nodeMain" }),
      h("div", { class: "nodeChildren", id: `${node.compoundKey}-children` }),
    );
    if (node.parentKey == null) {
      this.container.replaceChildren(element);
      return;
    }
    // After the nearest earlier sibling that's on the page (openers aren't), or first
    const siblings = this.kids.get(node.parentKey) ?? [];
    for (let i = siblings.indexOf(node.compoundKey) - 1; i >= 0; i--) {
      const previous = byId(siblings[i]);
      if (previous !== null) { previous.after(element); return; }
    }
    byId(`${node.parentKey}-children`)?.prepend(element);
  }

  private draw(node: ViewNode) {
    const element = byId(node.compoundKey);
    if (element === null) return;
    const main = element.querySelector<HTMLElement>(":scope > .nodeMain")!;
    const design = this.designs.find((candidate) => candidate.matches(node, this));
    const designName = design?.name ?? "generic";

    element.className = ["node", node.showAs ? `node${node.showAs}` : "", `prominence${node.prominence}`, node.open === false ? "closed" : ""].filter(Boolean).join(" ");
    element.dataset.design = designName;

    // Update a control in place when it's still the same kind of control, drawn by the same design
    if (node.control != null && main.dataset.shown === node.control && main.dataset.drawnBy === designName) {
      updateControl(node, main);
      this.dev.drawn(node, designName);
      return;
    }

    const hadFocus = main.contains(document.activeElement);
    const wasWhole = this.wholes.has(node.compoundKey);
    main.replaceChildren();
    main.dataset.shown = node.control ?? (node.childKeys.length > 0 ? "Heading" : "Text");
    main.dataset.drawnBy = designName;
    if (design !== undefined) {
      design.draw(node, main, this);
      if (design.whole) {
        this.wholes.add(node.compoundKey);
        byId(`${node.compoundKey}-children`)?.replaceChildren();
      }
      else {
        this.wholes.delete(node.compoundKey);
      }
    }
    else {
      this.wholes.delete(node.compoundKey);
      generic(node, main, this);
    }
    if (node.control != null) describe(node, main, this);
    if (hadFocus) main.querySelector<HTMLElement>("button, input, select, textarea")?.focus();
    this.dev.drawn(node, designName);
    // No longer drawn whole: its children need drawing for themselves
    if (wasWhole && !this.wholes.has(node.compoundKey)) this.drawChildren(node);
  }

  private drawChildren(node: ViewNode) {
    for (const child of this.children(node)) {
      if (child.isOpener) continue;
      this.create(child);
      this.draw(child);
      if (!this.wholes.has(child.compoundKey)) this.drawChildren(child);
    }
  }

  // A problem or help text came or went: what describes the controls beside it changed
  private redescribe(parentKey: string) {
    const parent = this.nodes.get(parentKey);
    if (parent === undefined) return;
    for (const child of this.children(parent)) {
      const main = byId(child.compoundKey)?.querySelector<HTMLElement>(":scope > .nodeMain");
      if (child.control != null && main) describe(child, main, this);
    }
  }

  /*
  Changes from other people are announced politely: new chat messages and new suggestions. Everything else (what the
  person did themselves) they already know about.
  */
  private announce(diff: ViewDiff) {
    // The first update is the whole page, not news
    if (this.first) { this.first = false; return; }
    if (this.options.announce === undefined) return;
    const said: string[] = [];
    for (const node of diff.added) {
      const message = this.child(node, "wording");
      if (this.node(node.parentKey)?.key === "chats" && message !== undefined) said.push(`${node.wording}: ${message.wording}`);
    }
    for (const node of diff.changed) if (node.key === "suggestions") said.push(node.wording);
    if (said.length > 0) this.options.announce.textContent = said.join(". ");
  }

}
